import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { XLogoIcon, PlusIcon, TrashIcon, SpinnerGapIcon, WarningIcon, MagnifyingGlassIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { fetchHandleWatch, addHandle, removeHandle, fetchAlertSettings } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { markHandlesSeen } from "../lib/useHandleAlerts";
import { useSeo } from "../lib/useSeo";
import { scoreColor, fmtAge } from "../lib/format";
import { verdictColor } from "../components/DegenScore";

const SUGGESTED = ["a1lon9", "pxblocito", "sullyfromDeets", "0xsolomon", "NotOnBooze"];

const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const MatchCard = ({ m }) => (
  <Link
    to={`/coin/${m.mint}`}
    data-testid={`handle-match-${m.symbol}`}
    className={`flex items-center justify-between gap-3 p-3 border transition-colors ${m.boost ? "border-neon/40 bg-neon/[0.05] hover:bg-neon/10" : "border-white/10 hover:border-neon/40 hover:bg-neon/[0.03]"}`}
  >
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-heading font-bold text-sm">{m.symbol || "?"}</span>
        <span className="text-xs text-muted-foreground truncate">{m.name}</span>
      </div>
      {m.boost && m.deployer ? (
        <div className="flex items-center gap-1 text-[10px] text-neon font-mono mt-0.5" title={(m.deployer.flags || []).join(" · ")}>
          ⭐ Proven deployer · {m.deployer.runnerCount} runner{m.deployer.runnerCount === 1 ? "" : "s"}
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground/60 font-mono mt-0.5">first seen {ago(m.firstSeenTs)} · {m.source}</div>
      )}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {m.score != null && (
        <div className="text-right">
          <div className="font-mono font-bold text-sm" style={{ color: scoreColor(m.score) }}>{m.score}</div>
          <div className="text-[9px]" style={{ color: verdictColor(m.verdict) }}>{m.verdict}</div>
        </div>
      )}
      <ArrowSquareOutIcon size={16} className="text-muted-foreground" />
    </div>
  </Link>
);

export default function HandleRadar() {
  useSeo({ title: "X Handle Radar · AlphaApeing", description: "Watch X handles and catch every token that links them in its socials — with a Degen Score and a verify-it's-really-them caveat." });
  const cid = getClientId();
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [provenOnly, setProvenOnly] = useState(false);

  const settingsQ = useQuery({ queryKey: ["alertSettings", cid], queryFn: () => fetchAlertSettings(cid) });
  const minScore = settingsQ.data?.handleMinScore ?? 0;

  const { data, isLoading } = useQuery({ queryKey: ["handleWatch", cid, showAll, provenOnly, minScore], queryFn: () => fetchHandleWatch(cid, showAll, provenOnly, minScore), refetchInterval: 60000 });

  const addMut = useMutation({
    mutationFn: (h) => addHandle(cid, h),
    onSuccess: (res) => {
      toast.success(`Watching @${res.handleRaw} · ${res.count} match${res.count === 1 ? "" : "es"}`);
      setInput("");
      qc.invalidateQueries({ queryKey: ["handleWatch", cid] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't add handle"),
  });

  const removeMut = useMutation({
    mutationFn: (h) => removeHandle(cid, h),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["handleWatch", cid] }),
  });

  const handles = data?.handles || [];
  const watchedLc = new Set(handles.map((h) => h.handle));
  const totalHidden = handles.reduce((a, h) => a + (h.hidden || 0), 0);

  // Clear the sidebar "new tokens" badge once the user views the radar.
  useEffect(() => {
    if (!data) return;
    const mints = [];
    for (const h of handles) for (const m of h.matches || []) if (m.mint) mints.push(m.mint);
    markHandlesSeen(mints);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <div className="flex items-center gap-2.5">
          <XLogoIcon size={28} weight="fill" className="text-neon" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">X HANDLE RADAR</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Watch the X accounts you care about. AlphaApeing surfaces every token that <span className="text-white">links that handle in its socials</span>,
          scores it, and pings your Telegram when a new one appears.
        </p>
      </div>

      <div className="border border-warn/30 bg-warn/[0.06] p-3.5 flex items-start gap-2.5" data-testid="handle-caveat">
        <WarningIcon size={18} className="text-warn shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          These are <span className="text-warn font-semibold">claims, not proof.</span> A token linking @handle does <span className="text-white">not</span> mean
          that person launched it — scammers spoof famous accounts. Always verify on the account's real profile before aping.
        </p>
      </div>

      {/* Add handle */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 border border-white/15 px-3 py-2 focus-within:border-neon/50 transition-colors max-w-md">
          <MagnifyingGlassIcon size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground text-sm">@</span>
          <input
            data-testid="handle-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && input.trim() && addMut.mutate(input.trim())}
            placeholder="add an X handle to watch"
            className="bg-transparent outline-none text-sm w-full"
          />
          <button
            data-testid="handle-add-btn"
            onClick={() => input.trim() && addMut.mutate(input.trim())}
            disabled={addMut.isPending || !input.trim()}
            className="flex items-center gap-1 px-2.5 py-1 bg-neon text-black text-xs font-semibold disabled:opacity-50"
          >
            {addMut.isPending ? <SpinnerGapIcon size={13} className="animate-spin" /> : <PlusIcon size={13} weight="bold" />} Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[11px] text-muted-foreground/70 self-center">Quick add:</span>
          {SUGGESTED.filter((s) => !watchedLc.has(s.toLowerCase())).map((s) => (
            <button
              key={s}
              data-testid={`handle-suggest-${s}`}
              onClick={() => addMut.mutate(s)}
              className="text-[11px] font-mono px-2 py-1 border border-white/12 text-muted-foreground hover:border-neon/40 hover:text-neon transition-colors"
            >
              @{s}
            </button>
          ))}
        </div>
      </div>

      {/* Watched handles */}
      {handles.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-white/10 pb-3" data-testid="filter-bar">
          <p className="text-[11px] text-muted-foreground">
            {provenOnly ? (
              <><span className="text-neon font-semibold">Proven deployers only</span> — showing tokens whose deployer has a runner track record.</>
            ) : (
              <><span className="text-white font-semibold">Quality filter {showAll ? "off" : "on"}</span> — hides low-score, thin-liquidity, spam-named & serial-spam tokens.</>
            )}
            {totalHidden > 0 && !showAll && <span className="text-warn"> {totalHidden} hidden.</span>}
          </p>
          <div className="flex items-center gap-2">
            <button
              data-testid="handle-proven-toggle"
              onClick={() => setProvenOnly((v) => !v)}
              className={`text-[11px] font-mono px-2.5 py-1 border transition-colors ${provenOnly ? "border-neon/60 bg-neon/10 text-neon" : "border-white/15 text-muted-foreground hover:border-neon/40 hover:text-neon"}`}
            >
              ⭐ {provenOnly ? "Proven only: ON" : "Proven deployers only"}
            </button>
            {!provenOnly && (
              <button
                data-testid="handle-filter-toggle"
                onClick={() => setShowAll((v) => !v)}
                className="text-[11px] font-mono px-2.5 py-1 border border-white/15 hover:border-neon/40 hover:text-neon transition-colors"
              >
                {showAll ? "Hide low-quality" : `Show all${totalHidden ? ` (+${totalHidden})` : ""}`}
              </button>
            )}
          </div>
        </div>
      )}

      {handles.length > 0 && !provenOnly && (
        <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground" data-testid="handle-minscore-note">
          <span>Score filter:</span>
          <span className="text-neon">{minScore === 0 ? "Default (≈50–60)" : `${minScore}+`}</span>
          <span className="text-muted-foreground/50">·</span>
          <Link to="/alerts" className="text-muted-foreground hover:text-neon transition-colors underline underline-offset-2" data-testid="handle-minscore-edit-link">
            change in Alerts
          </Link>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>
      ) : handles.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="handle-empty">
          <XLogoIcon size={30} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No handles yet. Add one above or tap a quick-add to start watching.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="handle-list">
          {handles.map((h) => (
            <div key={h.handle} className="border border-white/10 bg-card p-4" data-testid={`handle-block-${h.handle}`}>
              <div className="flex items-center justify-between mb-3">
                <a href={`https://x.com/${h.handleRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-heading font-bold text-lg hover:text-neon transition-colors">
                  <XLogoIcon size={18} weight="fill" /> @{h.handleRaw}
                  <span className="text-xs font-mono text-muted-foreground">· {h.count} token{h.count === 1 ? "" : "s"}{h.hidden > 0 && !showAll ? ` · ${h.hidden} filtered` : ""}</span>
                </a>
                <button
                  data-testid={`handle-remove-${h.handle}`}
                  onClick={() => removeMut.mutate(h.handle)}
                  className="p-1.5 text-muted-foreground hover:text-danger transition-colors"
                  title="Stop watching"
                >
                  <TrashIcon size={16} />
                </button>
              </div>
              {h.matches.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 text-center">No matching tokens yet — we'll ping your Telegram the moment one appears.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {h.matches.map((m) => <MatchCard key={m.mint} m={m} />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
        Coverage grows as tokens flow through AlphaApeing's radar and live launch feed. To get Telegram pings for new matches, enable alerts on the{" "}
        <Link to="/alerts" className="underline hover:text-neon">Alerts</Link> page.
      </p>
    </div>
  );
}
